import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

/**
 * AddLeadModal — manual lead entry. Enforces the "name + (email or phone)"
 * rule client-side so the toast is instant; backend has the same check.
 */
export const AddLeadModal = ({ open, onClose, chatbotId, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', source: 'manual', tags: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.email?.trim() && !formData.phone?.trim()) {
      toast.error('Please provide at least an email or phone number so this lead is reachable.');
      return;
    }
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        chatbot_id: chatbotId,
        tags: formData.tags ? formData.tags.split(',').map((t) => t.trim()) : [],
      };
      await api.post('/leads', payload);
      toast.success('Lead created successfully! 🎉');
      onSuccess();
      onClose();
      setFormData({ name: '', email: '', phone: '', company: '', source: 'manual', tags: '' });
    } catch (error) {
      toast.error('Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-blue-600" /> Add New Lead</DialogTitle>
          <DialogDescription>Manually add a lead to your pipeline</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Name *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Smith" className="mt-1" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@company.com" className="mt-1" data-testid="lead-form-email" />
              <p className="text-[11px] text-slate-500 mt-1">Required if no phone number is provided.</p>
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1 234 567 8900" className="mt-1" data-testid="lead-form-phone" />
            </div>
          </div>

          <div>
            <Label>Company</Label>
            <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} placeholder="Acme Inc." className="mt-1" />
          </div>

          <div>
            <Label>Source</Label>
            <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual Entry</SelectItem>
                <SelectItem value="web_chat">Website Chat</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="web_form">Web Form</SelectItem>
                <SelectItem value="api">API</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Tags (comma separated)</Label>
            <Input value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} placeholder="hot, priority, enterprise" className="mt-1" />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Creating...' : 'Create Lead'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
